/**
 * assets/handoffs/design_handoff_branding — restyle onto Untitled UI.
 * Structure/actions unchanged from the legacy page; see
 * OrganizationBrandingPage/ for the card sub-components.
 */
import React, { useEffect, useRef, useState } from "react";
import { useAppStore } from "./../../../hooks/redux/app";
import TabbedSettingsPage from "../templates/TabbedSettingsPage/TabbedSettingsPage";
import { WORKSPACE_SETTINGS_TABS } from "../templates/TabbedSettingsPage/settingsTabs";
import Form from "./OrganizationBrandingPage/Form";
import { useCurrentOrganizationStore } from "../../../hooks/redux/currentOrganization";

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

const fieldsFromSettings = (settings = {}) => ({
  logo: settings.logo || null,
  favicon: settings.favicon || null,
  brandColor: settings.brandColor || "",
  accentColor: settings.accentColor || "",
});

const OrganizationBrandingPage = () => {
  const { clearErrors } = useAppStore();
  const { currentOrganization, editOrganization, uploadBrandingFile } =
    useCurrentOrganizationStore();
  const orgModel = currentOrganization.model || {};

  const baseline = fieldsFromSettings(orgModel.settings || {});
  const [draft, setDraft] = useState(baseline);
  const [resetKey, setResetKey] = useState(0);
  // Local flag rather than deriving from the two separate redux loading
  // booleans below — those flip off between the sequential color-edit and
  // upload requests inside onSubmit, which would flicker the footer's
  // "Saving…" state off and on between each sub-request.
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  // Saving can involve up to three sequential requests (color edit, logo
  // upload, favicon upload) — refs stay current across each `await` inside
  // onSubmit so the draft resync at the end reads the truly final state,
  // not whichever request happened to finish first.
  const orgModelRef = useRef(orgModel);
  orgModelRef.current = orgModel;
  const currentOrganizationRef = useRef(currentOrganization);
  currentOrganizationRef.current = currentOrganization;

  useEffect(() => {
    return clearErrors;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setField = (field) => (value) => {
    setJustSaved(false);
    setDraft((prev) => ({ ...prev, [field]: value }));
  };

  const isDirty =
    draft.logo !== baseline.logo ||
    draft.favicon !== baseline.favicon ||
    draft.brandColor !== baseline.brandColor ||
    draft.accentColor !== baseline.accentColor;

  const colorsValid =
    (!draft.brandColor || HEX_RE.test(draft.brandColor)) &&
    (!draft.accentColor || HEX_RE.test(draft.accentColor));

  const uploading = currentOrganization.uploadBrandingFile || {};
  const uploadErrors = currentOrganization.uploadBrandingFileError || {};
  const anyUploading = uploading.logo || uploading.favicon;
  const saving = isSubmitting;

  const onCancel = () => {
    setDraft(baseline);
    setResetKey((prev) => prev + 1);
    setJustSaved(false);
    clearErrors();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    await editOrganization(orgModel.uuid, {
      settings: {
        ...orgModel.settings,
        brandColor: draft.brandColor || null,
        accentColor: draft.accentColor || null,
      },
    });

    if (draft.logo !== baseline.logo) {
      await uploadBrandingFile("logo", draft.logo);
    }
    if (draft.favicon !== baseline.favicon) {
      await uploadBrandingFile("favicon", draft.favicon);
    }

    setIsSubmitting(false);

    const latest = currentOrganizationRef.current;
    const hasError =
      latest.editError ||
      latest.uploadBrandingFileError?.logo ||
      latest.uploadBrandingFileError?.favicon;
    if (!hasError) {
      setDraft(fieldsFromSettings(orgModelRef.current.settings));
      setJustSaved(true);
    }
  };

  return (
    <TabbedSettingsPage
      title="Workspace settings"
      subtitle={
        orgModel.name &&
        `Applies to ${orgModel.name}. Other workspaces are unaffected.`
      }
      tabs={WORKSPACE_SETTINGS_TABS}
    >
      <Form
        resetKey={resetKey}
        draft={draft}
        setField={setField}
        onCancel={onCancel}
        onSubmit={onSubmit}
        isDirty={isDirty}
        colorsValid={colorsValid}
        saving={saving}
        anyUploading={anyUploading}
        uploading={uploading}
        uploadErrors={uploadErrors}
        editError={currentOrganization.editError}
        justSaved={justSaved}
      />
    </TabbedSettingsPage>
  );
};

export default OrganizationBrandingPage;
